import { useContext, useEffect, useState } from "react";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Formik } from "formik";
import { isEqual, isNil, omitBy } from "lodash-es";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";
import type {
  FormContextData,
  ManualPlaceOfSafetyRequest,
  MhaStatusState,
  PatientStateWithContext,
  S136PlaceOfSafetyType,
  UpdatePatientStateWithManualEventRequest,
} from "@thalamos/aspire-common";
import {
  DetainedStatuses,
  DischargeReason,
  formManualEventSchema,
  getExpiryDate,
  getRenewalDates,
  InScopeNonSection136Detentions,
  MhaStatus,
  s136PlaceOfSafetyTypeValues,
} from "@thalamos/aspire-common";

import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import { admissionFormContextLogic } from "../FormProgressPage/helpers/formContextLogic.js";
import { EditMhaStatusSuccessModal } from "./EditMhaStatusSuccessModal.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  DateField,
  Dropdown,
  FormLabel,
  FormTitle,
  HelperText,
  PopupDialog,
  PopupDialogTitle,
  ReadOnlyContent,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import {
  DateFormField,
  DateTimeFormField,
  FormFooterSection,
  HospitalPicker,
  PlaceOfSafetyPicker,
  ukInstantOfWallClock,
} from "~/components/form/index.js";
import { isEndedSection136ViaOutcome } from "~/components/layout/PatientBanner/PatientStateDisplay.js";
import { Container } from "~/components/layout/styleWrappers.js";
import { LoggedInUserContext, ManagedLocationsContext } from "~/Contexts.js";
import { useSection136PlaceOfSafety } from "~/hooks/apiCalls.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";
import { ukLocalFormatDateTime } from "~/util.js";

const getRenewalsDropdownOptions = () => {
  return [...Array(100).keys()].map((i) => {
    return {
      value: i + 1,
      label: (i + 1).toString(),
    };
  });
};

const renewalDropDownOptions = getRenewalsDropdownOptions();

// Section 136 can be extended by 1-12 hours (24h baseline, 36h absolute cap).
const s136ExtensionHoursOptions = [...Array(12).keys()].map((i) => ({
  value: i + 1,
  label: (i + 1).toString(),
}));

// The place-of-safety type options offered when a place is entered manually.
// Mirrors the Vision MET form's list.
const s136PlaceOfSafetyTypeOptions = [
  {
    value: "hbPos136",
    label: "Health Based Place of Safety (HBPOS/136 Suite)",
  },
  { value: "emergencyDepartment", label: "Emergency Department (A&E)" },
  {
    value: "custody",
    label: "Police Custody Suite (exceptional circumstances)",
  },
  {
    value: "policeStation",
    label: "Police Station (exceptional circumstances)",
  },
  { value: "privateHome", label: "Private home" },
];

// Labels for types no longer offered, kept so an older record that still holds
// one of them can be displayed (and not silently dropped) when re-editing.
const legacyPlaceOfSafetyTypeLabels: Record<string, string> = {
  returnedToMentalHealthUnit: "Returned to mental health unit",
  careHome: "Care home",
  other: "Other",
};

// Builds the dropdown options, including the currently-stored type as an extra
// option if it is not one of the offered values (prevents a blank dropdown and
// avoids losing a deprecated value on save).
const placeOfSafetyTypeOptions = (currentType: string | undefined) => {
  if (
    !currentType ||
    s136PlaceOfSafetyTypeOptions.some((o) => o.value === currentType)
  ) {
    return s136PlaceOfSafetyTypeOptions;
  }
  return [
    ...s136PlaceOfSafetyTypeOptions,
    {
      value: currentType,
      label: legacyPlaceOfSafetyTypeLabels[currentType] ?? currentType,
    },
  ];
};

type ExistingPlaceOfSafety = {
  name?: string;
  address?: string;
  postalCode?: string;
  odsCode?: string;
  trustId?: string;
  eMHACuratedPlaceOfSafetyId?: string;
  type?: string;
};

const isS136PlaceOfSafetyType = (
  value: string | undefined,
): value is S136PlaceOfSafetyType =>
  s136PlaceOfSafetyTypeValues.some((type) => type === value);

// Type to save: from the selected curated place, or the dropdown value for a
// manual entry.
export const resolveS136PlaceOfSafetyType = (args: {
  placeOfSafetyId: string | undefined;
  curatedPlacesOfSafety: readonly { id: string; posType?: string }[];
  manualType: string | undefined;
}): S136PlaceOfSafetyType | undefined => {
  const { placeOfSafetyId, curatedPlacesOfSafety, manualType } = args;
  const curatedType = placeOfSafetyId
    ? curatedPlacesOfSafety.find((pos) => pos.id === placeOfSafetyId)?.posType
    : undefined;
  const type = curatedType || manualType;
  return isS136PlaceOfSafetyType(type) ? type : undefined;
};

// Baseline Section 136 expiry (detention start + 24h). Reuses the shared
// getExpiryDate so the 24h baseline stays in one place and matches the
// server's default expiry calculation. getExpiryDate returns undefined for a
// missing start but throws on an unparseable one, so validity is checked here.
const getSection136BaseExpiry = (startDateTime: string | undefined | null) =>
  startDateTime && dayjs(startDateTime).isValid()
    ? getExpiryDate({
        MhaStatusType: MhaStatus.Section136,
        effectiveDateTime: startDateTime,
      })
    : undefined;

// Extended Section 136 expiry is the baseline + hours, capped at start + 36h
// (24h baseline + the 12h legal maximum extension).
const getSection136ExtendedExpiry = (
  startDateTime: string | undefined | null,
  extensionHours: number | undefined,
) => {
  const baseExpiry = getSection136BaseExpiry(startDateTime);
  if (!baseExpiry || !extensionHours) {
    return baseExpiry;
  }
  const requested = dayjs(baseExpiry).add(extensionHours, "hours");
  const cap = dayjs(startDateTime).add(36, "hours");
  return (requested.isAfter(cap) ? cap : requested).toISOString();
};

const expiryReasonOptions = [
  {
    value:
      "Detention extended during nearest relative displacement proceedings",
    label:
      "Detention extended during nearest relative displacement proceedings",
  },
  {
    value: "Patient absent without leave (AWOL)",
    label: "Patient absent without leave (AWOL)",
  },
  { value: "Other", label: "Other" },
];

function ExpiryOverrideControls({
  values,
  fieldProps,
  showExpiryOverrideDialog,
  setShowExpiryOverrideDialog,
  expiryCanBeOverridden,
}: {
  values: any;
  showExpiryOverrideDialog: boolean;
  setShowExpiryOverrideDialog: (value: boolean) => void;
  expiryCanBeOverridden: boolean;
  fieldProps: any;
}) {
  const theme = useTheme();

  const [expiryDateOverride, setExpiryDateOverride] = useState<string | null>(
    values.expiryOverrideDateTime ?? null,
  );
  const [expiryReason, setExpiryReason] = useState<string | null>(
    values.expiryOverrideReason ?? null,
  );
  const [expiryReasonDescription, setExpiryReasonDescription] = useState<
    string | null
  >(values.expiryOverrideReasonDescription ?? null);

  useEffect(() => {
    setExpiryDateOverride(values.expiryOverrideDateTime ?? null);
    setExpiryReason(values.expiryOverrideReason ?? null);
    setExpiryReasonDescription(values.expiryOverrideReasonDescription ?? null);
  }, [
    values.expiryOverrideDateTime,
    values.expiryOverrideReason,
    values.expiryOverrideReasonDescription,
  ]);

  const isValid =
    expiryDateOverride &&
    dayjs(expiryDateOverride).isValid() &&
    dayjs(expiryDateOverride).isAfter(dayjs(values.expiryDateTime)) &&
    expiryReason &&
    expiryReasonDescription;

  return (
    <>
      {values.expiryOverrideDateTime && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
          }}
        >
          <Tooltip
            title={`Expiry date has been overridden. Default would be ${ukLocalFormatDateTime(values.expiryDateTime)}`}
          >
            <Warning sx={{ color: theme.palette.warning.main }} />
          </Tooltip>
          <Button
            color="inherit"
            label="Clear Override"
            onClick={() => {
              fieldProps.setValues({
                ...values,
                expiryOverrideDateTime: undefined,
                expiryOverrideReason: undefined,
                expiryOverrideReasonDescription: undefined,
              });
            }}
          />
        </Box>
      )}
      {expiryCanBeOverridden && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            sx={{ ml: "1rem" }}
            variant="outlined"
            color="inherit"
            startIcon={ButtonIcon.create}
            label={
              values.expiryOverrideDateTime ? "Edit Override" : "Override Date"
            }
            onClick={() => {
              setShowExpiryOverrideDialog(true);
            }}
          />

          <PopupDialog
            open={showExpiryOverrideDialog}
            onClose={() => {
              setShowExpiryOverrideDialog(false);
            }}
            title="Override Expiry Date"
          >
            <PopupDialogTitle
              titleText="Override Expiry Date for Section"
              closeDialog={() => {
                setShowExpiryOverrideDialog(false);
              }}
            />
            <Box sx={{ mt: "1rem" }}>
              <DateField
                inputFormat={"DD/MM/YYYY"}
                minDate={dayjs(values.expiryDateTime).add(1, "day")}
                referenceDate={dayjs(values.expiryDateTime).add(1, "day")}
                label={
                  "Override Expiry Date (will be set to 23:59 on chosen date)"
                }
                onChange={(value: Dayjs | null) => {
                  if (value?.isValid()) {
                    setExpiryDateOverride(
                      dayjs(value).endOf("day").toISOString(),
                    );
                  }
                }}
                value={expiryDateOverride || null}
                name={"expiryOverrideDateTime"}
              />
            </Box>

            <Box sx={{ mt: "1rem" }}>
              <FormLabel label={"Reason for override (required)"} />
              <Dropdown
                name={"expiryOverrideReason"}
                values={expiryReasonOptions}
                selectedValue={expiryReason || ""}
                onChange={(z) => {
                  const selected = expiryReasonOptions.find(
                    (o) => o.value === z,
                  );
                  setExpiryReason(selected?.value ?? null);
                }}
              />
            </Box>
            <Box sx={{ mt: "1rem" }}>
              <FormLabel label={"Reason description (required)"} />
              <TextField
                rows="5"
                multiline
                value={expiryReasonDescription}
                fullWidth
                onChange={(e) => {
                  setExpiryReasonDescription(e.target.value);
                }}
              />
            </Box>
            <Box
              sx={{
                mt: "1rem",
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                label="Cancel"
                color="inherit"
                onClick={() => {
                  setShowExpiryOverrideDialog(false);
                }}
              />

              <Button
                label="Save"
                disabled={!isValid}
                onClick={() => {
                  fieldProps.setValues({
                    ...values,
                    expiryOverrideDateTime: expiryDateOverride,
                    expiryOverrideReason: expiryReason,
                    expiryOverrideReasonDescription: expiryReasonDescription,
                  });
                  setShowExpiryOverrideDialog(false);
                }}
              />
            </Box>
          </PopupDialog>
        </Box>
      )}
    </>
  );
}

export function EditMhaStatus({
  state,
  activeStates,
  isScrutiny,
  hasFormWithApplicationOrMedRec,
  formContext,
  reloadFormContext,
}: {
  state: PatientStateWithContext;
  // All of the patient's latest states. During dual status (Section 136 +
  // another detention) this has two entries; the dual-status detection needs
  // the full set, not just the row being edited (`state`). Defaults to
  // `[state]` for callers that don't supply it (stories/tests).
  activeStates?: PatientStateWithContext[];
  isScrutiny: boolean;
  hasFormWithApplicationOrMedRec?: boolean;
  formContext?: FormContextData;
  reloadFormContext?: () => void;
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMobileView } = useScreenDetection();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const userContext = useContext(LoggedInUserContext);
  const managedLocationsContext = useContext(ManagedLocationsContext);
  // Manually setting Section 136 here creates a 136, so it is a calculation
  // trigger: offer it only when the org both calculates 136
  // (section136MhaStatusCalculationEnabled) and displays MHA status
  // (patientStateEnabled, which already gates this edit UI).
  const orgConfig = userContext?.user?.sessionOrganisationConfiguration;
  const section136Enabled =
    (orgConfig?.section136MhaStatusCalculationEnabled ?? false) &&
    (orgConfig?.patientStateEnabled ?? false);

  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
    extraWarning?: string;
  } | null>(null);
  const [successFn, setSuccessFn] = useState<{
    confirmFn: () => void;
    title: string;
    body: string;
  } | null>(null);

  const [showExpiryOverrideDialog, setShowExpiryOverrideDialog] =
    useState(false);

  const { mhaStatus, hospital, episodeOfCare, trust } = state.state;

  // A discharge already recorded as a Section 136 discharge stays selectable
  // once the org stops calculating 136, so editing it shows what is stored.
  const offerSection136Discharge =
    section136Enabled ||
    episodeOfCare?.dischargeReason === DischargeReason.Section136Outcome;

  // Prefill from the priority-hierarchy winner rather than raw `state.event`
  // for consistency with what the patient banner shows — they can differ
  // once a later police-presence/Vision-arrival signal outranks whatever
  // event created this row.
  const { section136PlaceOfSafety: resolvedSection136PlaceOfSafety } =
    useSection136PlaceOfSafety({
      patientId: state.patientId,
      skip:
        !section136Enabled ||
        (mhaStatus.status !== MhaStatus.Section136 &&
          !isEndedSection136ViaOutcome(state)),
    });

  const hasRenewals = (mhaStatus: MhaStatusState) => {
    if (
      mhaStatus.status === MhaStatus.Section17A ||
      mhaStatus.status === MhaStatus.Section3
    ) {
      if (
        mhaStatus.numberOfRenewals === undefined ||
        mhaStatus.numberOfRenewals === 0
      ) {
        return false;
      }
      return true;
    }
    return undefined;
  };

  const getNumberOfRenewals = (
    status: MhaStatus,
    hasRenewals: boolean | undefined,
    numberOfRenewals: number | undefined,
  ) => {
    if (status === MhaStatus.Section17A || status === MhaStatus.Section3) {
      return hasRenewals === true ? numberOfRenewals : 0;
    }
    return undefined;
  };

  const getOriginalRenewalExpiry = (mhaStatus: MhaStatusState) => {
    if (
      (mhaStatus.status === MhaStatus.Section17A ||
        mhaStatus.status === MhaStatus.Section3) &&
      hasRenewals(mhaStatus) &&
      mhaStatus.initialStartDateTime
    ) {
      const { originalRenewalEndDate } = getRenewalDates(
        mhaStatus.initialStartDateTime,
        0,
      );
      return originalRenewalEndDate;
    }
    return undefined;
  };

  const hasBeenOverriddenAlready = state.expiryOverrideInfo;

  // Section 136 place-of-safety / police context lives in the event JSON, in
  // whichever shape the event type uses (see `PatientStateEvent`). Pull it out
  // so we can prefill the picker when editing an existing Section 136 or its
  // discharge.
  const existingS136 = state.event as
    | {
        placeOfSafety?: ExistingPlaceOfSafety;
        previousPlaceOfSafety?: ExistingPlaceOfSafety;
        section136Context?: {
          placeOfSafety?: ExistingPlaceOfSafety;
          policeContext?: { officerName?: string; forceName?: string };
        };
      }
    | undefined;
  const existingPos =
    resolvedSection136PlaceOfSafety?.placeOfSafety ??
    existingS136?.section136Context?.placeOfSafety ??
    existingS136?.placeOfSafety ??
    existingS136?.previousPlaceOfSafety;
  const existingPolice = existingS136?.section136Context?.policeContext;

  // Re-editing a Section 136: work out whether it was already extended and, if
  // it can be determined cleanly, by how many hours — from the gap between the
  // detention start and the expiry.
  //
  // The extension form lets the signer re-enter the detention start, so the
  // stored start and expiry may not differ by a whole number of hours (e.g.
  // police recorded 09:30 but the signer entered 10:00). When that happens we
  // can't trust a derived hour value, so we avoid pre-filling something wrong:
  //  - duration > 24h but not a whole-hour gap → it has clearly been extended,
  //    so pre-fill the question to "Yes" but leave the hours blank to force the
  //    user to confirm.
  //  - duration ≤ 24h with a non-whole-hour gap → we can't even be sure it was
  //    extended, so don't pre-fill the extended question at all.
  const section136ExtensionPrefill: {
    hasExtension: boolean | undefined;
    hours: number | undefined;
  } = (() => {
    if (
      mhaStatus.status !== MhaStatus.Section136 ||
      !mhaStatus.startDateTime ||
      !mhaStatus.expiryDateTime
    ) {
      return { hasExtension: undefined, hours: undefined };
    }
    const totalMinutes = dayjs(mhaStatus.expiryDateTime).diff(
      dayjs(mhaStatus.startDateTime),
      "minute",
    );
    if (totalMinutes === 24 * 60) {
      // Exactly 24h — not extended.
      return { hasExtension: false, hours: undefined };
    }
    if (totalMinutes < 24 * 60) {
      // Shorter than the 24h baseline (e.g. start re-entered later than the
      // police time) — ambiguous, leave the question for the user.
      return { hasExtension: undefined, hours: undefined };
    }
    // Longer than 24h — it has been extended.
    const extraMinutes = totalMinutes - 24 * 60;
    const isWholeHours = extraMinutes % 60 === 0;
    const extraHours = extraMinutes / 60;
    return {
      hasExtension: true,
      hours:
        isWholeHours && extraHours >= 1 && extraHours <= 12
          ? extraHours
          : undefined,
    };
  })();

  const initialData = {
    type: "manualEvent" as const,
    status:
      mhaStatus.status === MhaStatus.Expired ||
      mhaStatus.status === MhaStatus.Incomplete
        ? MhaStatus.Unknown
        : mhaStatus.status,
    startDateTime: mhaStatus.startDateTime || undefined,
    expiryDateTime: hasBeenOverriddenAlready
      ? state.expiryOverrideInfo?.defaultExpiryDateTime
      : mhaStatus.expiryDateTime || undefined,
    initialStartDateTime: mhaStatus.initialStartDateTime || undefined,
    numberOfRenewals: mhaStatus.numberOfRenewals || undefined,
    hasRenewals: hasRenewals(mhaStatus),
    ...(hospital
      ? {
          hospital: {
            hospitalId: hospital.id,
            trustId: trust?.id,
            trustName: trust?.name,
            trustOdsCode: trust?.odsCode,
            name: hospital.name,
            address: hospital.address,
            postalCode: hospital.postalCode,
            odsCode: hospital.odsCode || undefined,
            isConfirmed: hospital.name ? true : false,
          },
        }
      : { hospital: { name: "", isConfirmed: false } }),
    episodeOfCareDischargeDateTime:
      episodeOfCare?.dischargeDateTime || undefined,
    episodeOfCareDischargeReason: episodeOfCare?.dischargeReason || undefined,
    originalRenewalExpiry: getOriginalRenewalExpiry(mhaStatus),

    // Top-level field bound to the reused PlaceOfSafetyPicker. Type and
    // police context stay under section136Context (handled by the dropdown
    // and text fields below).
    placeOfSafety: existingPos
      ? {
          name: existingPos.name ?? "",
          address: existingPos.address ?? "",
          postalCode: existingPos.postalCode ?? "",
          odsCode: existingPos.odsCode ?? undefined,
          placeOfSafetyId: existingPos.eMHACuratedPlaceOfSafetyId ?? undefined,
          trustId: existingPos.trustId ?? trust?.id ?? undefined,
          trustName: trust?.name ?? undefined,
          isConfirmed: existingPos.name ? true : false,
        }
      : { name: "", isConfirmed: false },
    section136Context: {
      placeOfSafety: { type: existingPos?.type ?? undefined },
      ...(existingPolice
        ? {
            policeContext: {
              officerName: existingPolice.officerName,
              forceName: existingPolice.forceName,
            },
          }
        : {}),
    },
    hasSection136Extension: section136ExtensionPrefill.hasExtension,
    section136ExtensionHours: section136ExtensionPrefill.hours,

    ...(hasBeenOverriddenAlready
      ? {
          expiryOverrideDateTime:
            state.expiryOverrideInfo?.overrideExpiryDateTime,
          expiryOverrideReason: state.expiryOverrideInfo?.reason,
          expiryOverrideReasonDescription:
            state.expiryOverrideInfo?.reasonDescription,
        }
      : {
          expiryOverrideDateTime: undefined,
          expiryOverrideReason: undefined,
          expiryOverrideReasonDescription: undefined,
        }),
  };

  const updateMHAStatus = async (
    isOriginalFormValues: boolean,
    cleanedData: UpdatePatientStateWithManualEventRequest,
  ) => {
    if (!isOriginalFormValues) {
      const result = await api.patients.updatePatientMhaStateWithManualEvent(
        state.patientId,
        cleanedData,
      );
      if (result.status === 204) {
        renderSuccessToast({
          message: t("pages.editMHAStatus.successMessage"),
        });
      } else {
        renderErrorToast({
          message: t("pages.editMHAStatus.errorMessage"),
        });
        return;
      }
    }
  };

  const { hasActiveAmend, hasActiveDeMinimis } =
    formContext && formContext.type === "admission"
      ? admissionFormContextLogic(formContext, userContext?.user!)
      : { hasActiveAmend: false, hasActiveDeMinimis: false };

  // Also check for active de minimis on standalone/other form context types
  const activeDeMinimis =
    hasActiveDeMinimis ||
    formContext?.workItems?.some(
      (wi) =>
        wi.requestType === "de-minimis" &&
        (wi.status === "pending" || wi.status === "accepted"),
    );

  const scrutinyFlowConfirmAndSubmit = async (
    isOriginalFormValues: boolean,
    cleanedData: UpdatePatientStateWithManualEventRequest,
  ) => {
    const extraWarnings: string[] = [];
    if (hasActiveAmend) {
      extraWarnings.push(
        "This admission has an active Section 15 amend. This will be cancelled once you mark scrutiny as complete",
      );
    }
    if (activeDeMinimis) {
      extraWarnings.push(
        "There is an active de minimis correction in progress. This will be cancelled once you mark scrutiny as complete",
      );
    }

    setConfirmFn({
      extraWarning:
        extraWarnings.length > 0 ? extraWarnings.join(". ") : undefined,
      message: hasFormWithApplicationOrMedRec
        ? t("pages.editMHAStatus.scrutinyConfirmModalAdmission")
        : formContext?.type === "soad-assessment"
          ? t("pages.editMHAStatus.scrutinyConfirmModalSrf")
          : t("pages.editMHAStatus.scrutinyConfirmModalStandalone"),
      confirmFn: async () => {
        await updateMHAStatus(isOriginalFormValues, cleanedData);
        const result = await api.work.finalise(
          formContext!.activeTeamworkWorkItem!.id,
        );

        if (result.status === 204) {
          const itemName = qs.get("itemName") ?? "item";
          if (reloadFormContext) {
            reloadFormContext();
          }
          navigate(
            routeFns.formContextPageSuccessDialog(
              formContext!.id,
              formContext!.patientId,
              {
                title: "Administrative scrutiny marked complete",
                message: hasFormWithApplicationOrMedRec
                  ? "You have successfully marked administrative scrutiny of this admission complete"
                  : `You have successfully marked administrative scrutiny of this ${itemName} complete`,
              },
            ),
          );
        } else {
          renderErrorToast({
            message: "Error marking administrative scrutiny as complete",
          });
        }
      },
    });
  };

  return (
    <Box
      sx={{
        display: "block",
        marginBottom: theme.spacing(10),
      }}
    >
      {isScrutiny && (
        <Banner
          bannerType={BannerList.WARNING}
          body={[t("pages.editMHAStatus.scrutinyWarningMessage")]}
          sx={{
            marginBottom: theme.spacing(2),
          }}
        />
      )}
      <FormTitle
        useReducedTopPadding={true}
        hasTitleBottomMargin={false}
        titleText={t("pages.editMHAStatus.updateMHAStatus")}
        hasContainerMarginBottom={isMobileView ? false : true}
      />
      {confirmFn && (
        <ConfirmationModal
          extraWarning={confirmFn.extraWarning}
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}
      {successFn && (
        <EditMhaStatusSuccessModal
          title={successFn.title}
          body={successFn.body}
          confirmFn={successFn.confirmFn}
          closeFn={() => {
            setSuccessFn(null);
          }}
        />
      )}
      <Formik<any>
        validationSchema={formManualEventSchema}
        initialValues={initialData}
        onSubmit={async (values, { setSubmitting }) => {
          const isS136Discharge =
            values.status === MhaStatus.NotDetained &&
            values.episodeOfCareDischargeReason ===
              DischargeReason.Section136Outcome;
          const placeOfSafety: ManualPlaceOfSafetyRequest | undefined = values
            .placeOfSafety?.name
            ? {
                name: values.placeOfSafety.name,
                address: values.placeOfSafety.address,
                postalCode: values.placeOfSafety.postalCode,
                odsCode: values.placeOfSafety.odsCode || undefined,
                trustId: values.placeOfSafety.trustId,
                trustName: values.placeOfSafety.trustName || undefined,
                type: resolveS136PlaceOfSafetyType({
                  placeOfSafetyId: values.placeOfSafety?.placeOfSafetyId,
                  curatedPlacesOfSafety:
                    managedLocationsContext.curatedPlacesOfSafety,
                  manualType: values.section136Context?.placeOfSafety?.type,
                }),
              }
            : undefined;

          const cleanedData: UpdatePatientStateWithManualEventRequest = {
            type: "manualEvent" as const,
            status: values.status,
            startDateTime: values.startDateTime
              ? dayjs(values.startDateTime).toISOString()
              : undefined,
            initialStartDateTime: values.initialStartDateTime
              ? dayjs(values.initialStartDateTime).toISOString()
              : undefined,
            numberOfRenewals: getNumberOfRenewals(
              values.status,
              values.hasRenewals,
              values.numberOfRenewals,
            ),
            episodeOfCareDischargeDateTime:
              values.episodeOfCareDischargeDateTime || undefined,
            episodeOfCareDischargeReason:
              values.episodeOfCareDischargeReason || undefined,
            ...(!isS136Discharge &&
              values.hospital &&
              values.hospital.name && {
                hospital: {
                  hospitalId: values.hospital.hospitalId,
                  trustId: values.hospital.trustId,
                  name: values.hospital.name,
                  address: values.hospital.address,
                  postalCode: values.hospital.postalCode,
                  odsCode: values.hospital.odsCode || undefined,
                },
              }),
            expiryOverride: values.expiryOverrideDateTime
              ? {
                  expiryDateTime: values.expiryOverrideDateTime,
                  reason: values.expiryOverrideReason,
                  reasonDescription: values.expiryOverrideReasonDescription,
                }
              : undefined,
            ...(isS136Discharge &&
              placeOfSafety && { previousPlaceOfSafety: placeOfSafety }),
            ...(values.status === MhaStatus.Section136 &&
              placeOfSafety && {
                section136Context: {
                  placeOfSafety,
                  // Officer name is no longer collected, but preserve any
                  // pre-existing value rather than dropping stored data. The
                  // police context is kept whenever a force is recorded.
                  policeContext: (() => {
                    const officerName =
                      values.section136Context?.policeContext?.officerName ||
                      undefined;
                    const forceName =
                      values.section136Context?.policeContext?.forceName ||
                      undefined;
                    return forceName || officerName
                      ? { officerName, forceName }
                      : undefined;
                  })(),
                },
              }),
            ...(values.status === MhaStatus.Section136 &&
              values.hasSection136Extension &&
              values.section136ExtensionHours && {
                section136ExtensionHours: values.section136ExtensionHours,
              }),
          };

          // Runs the actual confirm/submit flow. `endExistingDetention`
          // decides whether the new status ends the patient's active status
          // in the *other* bucket (Section 136 vs non-136) — collapsing a
          // would-be dual status into a single one — or leaves it running
          // alongside (dual status). `undefined` leaves bucketing untouched.
          const runSubmit = async (endExistingDetention?: boolean) => {
            const requestData: UpdatePatientStateWithManualEventRequest =
              endExistingDetention === undefined
                ? cleanedData
                : { ...cleanedData, endExistingDetention };

            if (!isScrutiny) {
              setConfirmFn({
                message: t(`pages.editMHAStatus.confirmAction`),
                confirmFn: async () => {
                  setSubmitting(true);
                  const saved = await api.patients
                    .updatePatientMhaStateWithManualEvent(
                      state.patientId,
                      requestData,
                    )
                    .then(
                      (result) => result.status === 204,
                      () => false,
                    );
                  setSubmitting(false);

                  if (saved) {
                    setSuccessFn({
                      title: t(`pages.editMHAStatus.success`),
                      body: t(`pages.editMHAStatus.successModalBody`),
                      confirmFn: () => {
                        setSuccessFn(null);
                        navigate(routeFns.patientHome(state.patientId));
                      },
                    });
                  } else {
                    renderErrorToast({
                      message: t("pages.editMHAStatus.errorMessage"),
                    });
                  }

                  setConfirmFn(null);
                },
              });
            } else {
              // date/ time pickers set the value to null so need to remove before checking for differences
              const isOriginalFormValues = isEqual(
                omitBy(values, isNil),
                omitBy(initialData, isNil),
              );

              setSubmitting(true);
              await scrutinyFlowConfirmAndSubmit(
                isOriginalFormValues,
                requestData,
              );
              setSubmitting(false);
            }
          };

          // Look across ALL the patient's latest states, not just the row
          // being edited — otherwise a dual-status patient's "other" status
          // is invisible here and can never be ended.
          const now = dayjs();
          const activeStatuses = (activeStates ?? [state])
            .filter(
              (s) =>
                s.isLatest &&
                (!s.state.mhaStatus.expiryDateTime ||
                  dayjs(s.state.mhaStatus.expiryDateTime).isAfter(now)),
            )
            .map((s) => s.state.mhaStatus.status);

          const hasActiveSection136 = activeStatuses.includes(
            MhaStatus.Section136,
          );
          const activeNon136Detention = activeStatuses.find((s) =>
            InScopeNonSection136Detentions.includes(s),
          );

          const settingSection136 = values.status === MhaStatus.Section136;
          const settingNon136Detention =
            InScopeNonSection136Detentions.includes(values.status);

          const isCurrentlyDual =
            hasActiveSection136 && activeNon136Detention !== undefined;

          // Section 136 and a non-136 detention occupy separate buckets and
          // can run concurrently. `endExistingDetention` ends whatever is in
          // the bucket OPPOSITE the new status; this is that status. (The
          // new status replaces its own-bucket status automatically — the
          // choice is only about the opposite one.)
          const oppositeBucketStatus: MhaStatus | undefined = settingSection136
            ? activeNon136Detention
            : hasActiveSection136
              ? MhaStatus.Section136
              : undefined;

          if (oppositeBucketStatus === undefined) {
            // Nothing in the opposite bucket — no dual to create or resolve.
            await runSubmit(undefined);
          } else if (settingSection136 || settingNon136Detention) {
            if (isCurrentlyDual) {
              // The patient is already in a dual status (an automatically-
              // created Section 136 alongside a detention). Editing one leg
              // must leave the other running rather than collapse it.
              await runSubmit(false);
            } else {
              // Setting a detaining status while the opposite bucket holds an
              // active detention would create a dual status, which cannot be
              // set manually. End the existing detention (single status), the
              // same silent replacement a same-bucket change makes.
              await runSubmit(true);
            }
          } else {
            // Non-detaining status (Not Detained / Unknown) over an active
            // Section 136 — no meaningful dual to keep, so always end it.
            await runSubmit(true);
          }
        }}
      >
        {(formikData) => {
          const {
            values,
            setValues,
            setFieldValue,
            errors,
            touched,
            submitForm,
            isSubmitting,
            setFieldTouched,
            handleBlur,
            setTouched,
            dirty,
          } = formikData;

          const fieldProps = {
            context: {},
            values,
            validationSchema: formManualEventSchema,
            setValues,
            errors,
            touched,
            handleBlur,
            setFieldTouched,
          };

          const isSection5_4OrSection5_2OrSection4 =
            values.status === MhaStatus.Section5_4 ||
            values.status === MhaStatus.Section5_2 ||
            values.status === MhaStatus.Section4;

          const isSection2 = values.status === MhaStatus.Section2;

          const isSection136 = values.status === MhaStatus.Section136;

          const isSection3OrCTO =
            values.status === MhaStatus.Section17A ||
            values.status === MhaStatus.Section3;

          const isNotDetained = values.status === MhaStatus.NotDetained;

          const isS136Discharge =
            isNotDetained &&
            values.episodeOfCareDischargeReason ===
              DischargeReason.Section136Outcome;

          const dischargeReasonOptions = [
            {
              reason: DischargeReason.Section23,
              label: t("pages.editMHAStatus.dischargedSection23"),
            },
            {
              reason: DischargeReason.HoldingPowersDischarge,
              label: t("pages.editMHAStatus.dischargedHoldingPowers"),
            },
            ...(offerSection136Discharge
              ? [
                  {
                    reason: DischargeReason.Section136Outcome,
                    label: t("pages.editMHAStatus.dischargedSection136"),
                  },
                ]
              : []),
            {
              reason: DischargeReason.TribunalOrNearestRelative,
              label: t("pages.editMHAStatus.dischargedTribunal"),
            },
            {
              reason: DischargeReason.Other,
              label: t("pages.editMHAStatus.other"),
            },
          ];

          const placeOfSafetyFields = (label: string) => (
            <>
              <PlaceOfSafetyPicker
                field={{
                  type: "place-of-safety-picker",
                  field: "placeOfSafety",
                  label,
                }}
                fieldProps={fieldProps}
              />
              {/* Only ask for the type when the place is entered manually. */}
              {!values.placeOfSafety?.placeOfSafetyId && (
                <Box sx={{ marginTop: theme.spacing(2) }}>
                  <Dropdown
                    label="Place of safety type"
                    name="section136Context.placeOfSafety.type"
                    placeholder="Select a place of safety type"
                    selectedValue={
                      values.section136Context?.placeOfSafety?.type ?? ""
                    }
                    values={placeOfSafetyTypeOptions(
                      values.section136Context?.placeOfSafety?.type,
                    )}
                    onChange={(value) =>
                      setFieldValue(
                        "section136Context.placeOfSafety.type",
                        value,
                      )
                    }
                  />
                </Box>
              )}
            </>
          );

          const isPendingCto =
            values.status === MhaStatus.Section17A &&
            !values.hasRenewals &&
            values.startDateTime &&
            dayjs(values.startDateTime).isValid() &&
            dayjs(values.startDateTime).isAfter(dayjs()) &&
            dayjs(values.startDateTime).isBefore(dayjs().add(2, "month"));

          const isRenewalPendingCTO =
            values.status === MhaStatus.Section17A &&
            values.hasRenewals &&
            values.startDateTime &&
            dayjs(values.startDateTime).isValid() &&
            dayjs(values.startDateTime).isAfter(dayjs()) &&
            dayjs(values.startDateTime).isBefore(dayjs().add(2, "month"));

          const isRenewalPendingSection3 =
            values.status === MhaStatus.Section3 &&
            values.hasRenewals &&
            values.startDateTime &&
            dayjs(values.startDateTime).isValid() &&
            dayjs(values.startDateTime).isAfter(dayjs()) &&
            dayjs(values.startDateTime).isBefore(dayjs().add(2, "month"));

          const isPendingState =
            isPendingCto || isRenewalPendingCTO || isRenewalPendingSection3;

          const showDivider = DetainedStatuses.includes(values.status)
            ? true
            : false;

          // Section 136 and its discharge carry POS context in the event JSON,
          // not in hospital/trust — hide the hospital picker for them.
          const showHospital = DetainedStatuses.includes(values.status)
            ? values.status !== MhaStatus.Section136 && !isS136Discharge
            : false;

          // There may be expiry errors here (e.g. expiry in the past)
          // but we still allow the overriding, in case the override fixes it
          const expiryCanBeOverridden =
            [
              MhaStatus.Section2,
              MhaStatus.Section3,
              MhaStatus.Section17A,
            ].includes(values.status) &&
            values.expiryDateTime &&
            values.startDateTime &&
            dayjs(values.startDateTime).isBefore(dayjs());

          const getStartDateLabel = (status: MhaStatus): string => {
            return status === MhaStatus.Section17A
              ? t("pages.editMHAStatus.ctoStartDate")
              : t("pages.editMHAStatus.section3StartDate");
          };

          const getEndDateLabel = (status: MhaStatus): string => {
            return status === MhaStatus.Section17A
              ? t("pages.editMHAStatus.ctoExpiryDate")
              : t("pages.editMHAStatus.section3ExpiryDate");
          };

          const getOriginalStartDateLabel = (status: MhaStatus): string => {
            return status === MhaStatus.Section17A
              ? t("pages.editMHAStatus.originalCTOStartDate")
              : t("pages.editMHAStatus.originalSection3StartDate");
          };

          const getOriginalEndDateLabel = (status: MhaStatus): string => {
            return status === MhaStatus.Section17A
              ? t("pages.editMHAStatus.originalCTOExpiryDate")
              : t("pages.editMHAStatus.originalSection3ExpiryDate");
          };

          const getRenewalLabel = (status: MhaStatus): string => {
            return status === MhaStatus.Section17A
              ? t("pages.editMHAStatus.hasRenewalCTO")
              : t("pages.editMHAStatus.hasRenewalSection3");
          };

          const getRenewalDropdownLabel = (status: MhaStatus): string => {
            return status === MhaStatus.Section17A
              ? t("pages.editMHAStatus.extensions")
              : t("pages.editMHAStatus.renewals");
          };

          const getReadOnlyContent = (
            label: string,
            content: string | undefined | null,
            format: string,
            testId: string,
          ) => {
            const style = {
              marginLeft: isMobileView ? 0 : theme.spacing(6),
            };
            return (
              <Box
                data-testid={testId}
                sx={[
                  {
                    display: "flex",
                    flexDirection: "column",
                  },
                  ...(Array.isArray(style) ? style : [style]),
                ]}
              >
                <ReadOnlyContent
                  label={label}
                  content={[formatDateTime(content, format)]}
                  isActive
                  useMarginTop={true}
                />
              </Box>
            );
          };

          const setExpiryDateTime = (
            status: MhaStatus,
            startDateTime: string | undefined | null,
            numberOfRenewals?: number,
          ) => {
            if (startDateTime && dayjs(startDateTime).isValid()) {
              return getExpiryDate({
                MhaStatusType: status,
                effectiveDateTime: startDateTime,
                numberOfRenewals: numberOfRenewals,
              });
            }
            return undefined;
          };

          const getSelectedStatus = (status: MhaStatus) => {
            if (
              status === MhaStatus.Expired ||
              status === MhaStatus.Incomplete
            ) {
              return MhaStatus.Unknown;
            }
            return status;
          };

          const formatDateTime = (
            dateTime: string | undefined | null,
            format: string,
          ) => {
            return dateTime && dayjs(dateTime).isValid()
              ? dayjs(dateTime).tz("Europe/London").format(format)
              : "-- -- --";
          };

          return (
            <>
              <Container
                sx={isMobileView ? { marginTop: theme.spacing(2) } : {}}
              >
                <Dropdown
                  label={t("pages.editMHAStatus.currentMHAStatus")}
                  name="mhaStatus"
                  selectedValue={getSelectedStatus(values.status)}
                  values={[
                    {
                      value: MhaStatus.Unknown,
                      label: t("pages.editMHAStatus.unknown"),
                    },
                    {
                      value: MhaStatus.Section5_4,
                      label: t("pages.editMHAStatus.section5_4"),
                    },
                    {
                      value: MhaStatus.Section5_2,
                      label: t("pages.editMHAStatus.section5_2"),
                    },
                    {
                      value: MhaStatus.Section4,
                      label: t("pages.editMHAStatus.section4"),
                    },
                    {
                      value: MhaStatus.Section2,
                      label: t("pages.editMHAStatus.section2"),
                    },
                    {
                      value: MhaStatus.Section3,
                      label: t("pages.editMHAStatus.section3"),
                    },
                    {
                      value: MhaStatus.Section17A,
                      label: t("pages.editMHAStatus.cto"),
                    },
                    ...(section136Enabled
                      ? [
                          {
                            value: MhaStatus.Section136,
                            label: "Section 136",
                          },
                        ]
                      : []),
                    {
                      value: MhaStatus.NotDetained,
                      label: t("pages.editMHAStatus.notDetained"),
                    },
                  ]}
                  onChange={(value) => {
                    // reset everything
                    void setTouched({}, false);
                    void setValues({
                      type: "manualEvent" as const,
                      status: value as MhaStatus,
                      startDateTime: undefined,
                      expiryDateTime: undefined,
                      numberOfRenewals: undefined,
                      hasRenewals: undefined,
                      episodeOfCareDischargeDateTime: undefined,
                      episodeOfCareDischargeReason: undefined,
                      hospital: undefined,
                      originalRenewalExpiry: undefined,
                      initialStartDateTime: undefined,
                      expiryOverrideDateTime: undefined,
                      expiryOverrideReason: undefined,
                      expiryOverrideReasonDescription: undefined,
                      section136Context: undefined,
                      hasSection136Extension: undefined,
                      section136ExtensionHours: undefined,
                    });
                  }}
                />

                <>
                  {showDivider && (
                    <Divider
                      sx={{
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                      }}
                    />
                  )}

                  {(isSection5_4OrSection5_2OrSection4 || isSection136) && (
                    <Box
                      sx={[
                        {
                          display: "flex",
                          flexDirection: isMobileView ? "column" : "row",
                        },
                        isMobileView ? { marginBottom: theme.spacing(3) } : {},
                      ]}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <DateTimeFormField
                          field={{
                            label: t("pages.editMHAStatus.startDateTime"),
                            type: "date-time",
                            field: "startDateTime",
                            showDateTimeMessage: false,
                            maximum: () => dayjs(),
                            minimum: () =>
                              values.status === MhaStatus.Section5_4
                                ? dayjs().subtract(6, "hours")
                                : values.status === MhaStatus.Section136
                                  ? dayjs().subtract(24, "hours")
                                  : dayjs().subtract(3, "days"),
                          }}
                          fieldProps={{
                            ...fieldProps,
                            setValues: (values: any) => {
                              if (dayjs(values.startDateTime).isValid()) {
                                void setFieldTouched(
                                  "expiryDateTime",
                                  true,
                                  false,
                                );
                              }
                              void setValues({
                                ...values,
                                startDateTime: values.startDateTime,
                                expiryDateTime:
                                  values.status === MhaStatus.Section136 &&
                                  values.hasSection136Extension
                                    ? getSection136ExtendedExpiry(
                                        values.startDateTime,
                                        values.section136ExtensionHours,
                                      )
                                    : setExpiryDateTime(
                                        values.status,
                                        values.startDateTime,
                                      ),
                              });
                            },
                          }}
                        />
                      </Box>

                      {/* When a Section 136 has been extended we instead show
                          the "Original" and "Current" end dates below, so the
                          plain end date here would be redundant. */}
                      {!(
                        isSection136 && values.hasSection136Extension === true
                      ) && (
                        <Box
                          data-testid="endDateTime"
                          sx={[
                            {
                              display: "flex",
                              flexDirection: "column",
                            },
                            isMobileView
                              ? {}
                              : { marginLeft: theme.spacing(6) },
                          ]}
                        >
                          <ReadOnlyContent
                            label={t("pages.editMHAStatus.endDateTime")}
                            content={[
                              formatDateTime(
                                values.expiryDateTime,
                                "DD/MM/YY - HH:mm",
                              ),
                            ]}
                            isActive
                            useMarginTop={true}
                          />

                          {touched.expiryDateTime && errors.expiryDateTime && (
                            <HelperText
                              errorMessage={errors.expiryDateTime as string}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {isSection136 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: theme.spacing(2),
                      }}
                    >
                      <FormLabel
                        label={t("pages.editMHAStatus.section136Extended")}
                      />
                      <RadioGroup
                        aria-labelledby="radio-buttons-group-label"
                        name="hasSection136Extension"
                        defaultValue={null}
                        row={true}
                      >
                        <FormControlLabel
                          control={<Radio sx={{ color: "primary.main" }} />}
                          label={t("common.no")}
                          value={false}
                          checked={values.hasSection136Extension === false}
                          name="hasSection136Extension"
                          onChange={() => {
                            void setValues({
                              ...values,
                              hasSection136Extension: false,
                              section136ExtensionHours: undefined,
                              expiryDateTime: getSection136BaseExpiry(
                                values.startDateTime,
                              ),
                            });
                          }}
                        />
                        <FormControlLabel
                          control={<Radio sx={{ color: "primary.main" }} />}
                          label={t("common.yes")}
                          value={true}
                          checked={values.hasSection136Extension === true}
                          name="hasSection136Extension"
                          onChange={() => {
                            void setValues({
                              ...values,
                              hasSection136Extension: true,
                            });
                          }}
                        />
                      </RadioGroup>
                      {touched.hasSection136Extension &&
                        errors.hasSection136Extension && (
                          <HelperText
                            errorMessage={
                              errors.hasSection136Extension as string
                            }
                          />
                        )}

                      {values.hasSection136Extension === true && (
                        <>
                          <Box
                            sx={{
                              marginTop: theme.spacing(2),
                              maxWidth: theme.spacing(40),
                            }}
                          >
                            <Dropdown
                              label={t(
                                "pages.editMHAStatus.section136ExtensionHours",
                              )}
                              name="section136ExtensionHours"
                              selectedValue={
                                values.section136ExtensionHours === undefined
                                  ? ""
                                  : values.section136ExtensionHours
                              }
                              values={s136ExtensionHoursOptions}
                              onChange={(value) => {
                                const hours = value as number;
                                void setValues({
                                  ...values,
                                  section136ExtensionHours: hours,
                                  expiryDateTime: getSection136ExtendedExpiry(
                                    values.startDateTime,
                                    hours,
                                  ),
                                });
                              }}
                              errorMessage={
                                touched.section136ExtensionHours &&
                                errors.section136ExtensionHours
                                  ? (errors.section136ExtensionHours as string)
                                  : undefined
                              }
                            />
                          </Box>
                          <Box
                            sx={[
                              {
                                display: "flex",
                                flexDirection: isMobileView ? "column" : "row",
                                marginTop: theme.spacing(2),
                              },
                            ]}
                          >
                            <Box
                              data-testid="originalSection136EndDate"
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              <ReadOnlyContent
                                label={t(
                                  "pages.editMHAStatus.originalSection136EndDate",
                                )}
                                content={[
                                  formatDateTime(
                                    getSection136BaseExpiry(
                                      values.startDateTime,
                                    ),
                                    "DD/MM/YY - HH:mm",
                                  ),
                                ]}
                                isActive
                                useMarginTop={true}
                              />
                            </Box>
                            <Box
                              data-testid="currentSection136EndDate"
                              sx={[
                                { display: "flex", flexDirection: "column" },
                                isMobileView
                                  ? {}
                                  : { marginLeft: theme.spacing(6) },
                              ]}
                            >
                              <ReadOnlyContent
                                label={t(
                                  "pages.editMHAStatus.currentSection136EndDate",
                                )}
                                content={[
                                  formatDateTime(
                                    // Until the user picks the number of hours,
                                    // show the actual stored expiry rather than
                                    // recomputing to the 24h baseline (which
                                    // would understate an extension whose hours
                                    // we couldn't derive).
                                    values.section136ExtensionHours
                                      ? getSection136ExtendedExpiry(
                                          values.startDateTime,
                                          values.section136ExtensionHours,
                                        )
                                      : values.expiryDateTime,
                                    "DD/MM/YY - HH:mm",
                                  ),
                                ]}
                                isActive
                                useMarginTop={true}
                              />

                              {/* The plain end-date box (which normally hosts
                                  this error) is hidden while extended, so show
                                  the expiry-in-past error against the current
                                  end date here instead. */}
                              {touched.expiryDateTime &&
                                errors.expiryDateTime && (
                                  <HelperText
                                    errorMessage={
                                      errors.expiryDateTime as string
                                    }
                                  />
                                )}
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}

                  {isSection136 && (
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {placeOfSafetyFields("Place of Safety")}
                      <Box sx={{ marginTop: theme.spacing(3) }}>
                        <FormLabel label="Police force (optional)" />
                      </Box>
                      <TextField
                        label="Force"
                        size="small"
                        value={
                          values.section136Context?.policeContext?.forceName ??
                          ""
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "section136Context.policeContext.forceName",
                            e.target.value,
                          )
                        }
                        sx={{ marginTop: theme.spacing(2) }}
                      />
                    </Box>
                  )}

                  {isSection2 && (
                    <Box
                      sx={[
                        {
                          display: "flex",
                          flexDirection: isMobileView ? "column" : "row",
                        },
                        isMobileView ? { marginBottom: theme.spacing(3) } : {},
                      ]}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <DateFormField
                          field={{
                            label: t("pages.editMHAStatus.startDate"),
                            type: "date",
                            field: "startDateTime",
                            maximum: () => dayjs(),
                            minimum: () => dayjs().subtract(28, "days"),
                          }}
                          fieldProps={{
                            ...fieldProps,
                            setValues: (values: any) => {
                              let utcStartDate;
                              if (
                                values.startDateTime &&
                                dayjs(values.startDateTime).isValid()
                              ) {
                                utcStartDate = ukInstantOfWallClock(
                                  dayjs(values.startDateTime),
                                ).toISOString();
                                void setFieldTouched(
                                  "expiryDateTime",
                                  true,
                                  false,
                                );
                              }
                              void setValues({
                                ...values,
                                startDateTime: utcStartDate,
                                expiryDateTime: setExpiryDateTime(
                                  values.status,
                                  utcStartDate,
                                ),
                                expiryOverrideDateTime: undefined,
                                expiryOverrideReason: undefined,
                                expiryOverrideReasonDescription: undefined,
                              });
                            },
                          }}
                        />
                      </Box>

                      <Box
                        data-testid="endDate"
                        sx={{
                          display: "flex",
                          flexDirection: "column",

                          ...(isMobileView
                            ? {}
                            : { marginLeft: theme.spacing(6) }),
                        }}
                      >
                        <>
                          <ReadOnlyContent
                            label={t("pages.editMHAStatus.endDate")}
                            content={[
                              (values.expiryOverrideDateTime ??
                              values.expiryDateTime)
                                ? ukLocalFormatDateTime(
                                    values.expiryOverrideDateTime ??
                                      values.expiryDateTime,
                                  )
                                : "-- -- --",
                            ]}
                            isActive
                            useMarginTop={true}
                          />

                          {touched.expiryDateTime && errors.expiryDateTime && (
                            <HelperText
                              errorMessage={errors.expiryDateTime as string}
                            />
                          )}
                        </>
                      </Box>
                      <ExpiryOverrideControls
                        values={values}
                        fieldProps={fieldProps}
                        expiryCanBeOverridden={expiryCanBeOverridden}
                        showExpiryOverrideDialog={showExpiryOverrideDialog}
                        setShowExpiryOverrideDialog={
                          setShowExpiryOverrideDialog
                        }
                      />
                    </Box>
                  )}

                  {isSection3OrCTO && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          marginBottom: theme.spacing(2),
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flex: 1,
                          }}
                        >
                          <Box>
                            <FormLabel label={getRenewalLabel(values.status)} />
                            <RadioGroup
                              aria-labelledby="radio-buttons-group-label"
                              name="hasRenewals"
                              defaultValue={null}
                              row={true}
                            >
                              <FormControlLabel
                                control={
                                  <Radio sx={{ color: "primary.main" }} />
                                }
                                label={t("common.no")}
                                value={false}
                                checked={values.hasRenewals === false}
                                name="hasRenewals"
                                onChange={() => {
                                  // reset when moving from yes to no
                                  void setTouched({}, false);
                                  void setValues({
                                    ...values,
                                    hasRenewals: false,
                                    startDateTime: undefined,
                                    expiryDateTime: undefined,
                                    numberOfRenewals: undefined,
                                    episodeOfCareDischargeDateTime: undefined,
                                    episodeOfCareDischargeReason: undefined,
                                    hospital: undefined,
                                    originalRenewalExpiry: undefined,
                                    initialStartDateTime: undefined,
                                    expiryOverrideDateTime: undefined,
                                    expiryOverrideReason: undefined,
                                    expiryOverrideReasonDescription: undefined,
                                  });
                                }}
                              ></FormControlLabel>
                              <FormControlLabel
                                control={
                                  <Radio sx={{ color: "primary.main" }} />
                                }
                                label={t("common.yes")}
                                value={true}
                                checked={values.hasRenewals === true}
                                name="hasRenewals"
                                onChange={() => {
                                  // reset when moving from yes to no
                                  void setTouched({}, false);
                                  void setValues({
                                    ...values,
                                    hasRenewals: true,
                                    startDateTime: undefined,
                                    expiryDateTime: undefined,
                                    numberOfRenewals: undefined,
                                    episodeOfCareDischargeDateTime: undefined,
                                    episodeOfCareDischargeReason: undefined,
                                    hospital: undefined,
                                    originalRenewalExpiry: undefined,
                                    initialStartDateTime: undefined,
                                    expiryOverrideDateTime: undefined,
                                    expiryOverrideReason: undefined,
                                    expiryOverrideReasonDescription: undefined,
                                  });
                                }}
                              />
                            </RadioGroup>
                            {touched.hasRenewals && errors.hasRenewals && (
                              <HelperText
                                errorMessage={errors.hasRenewals as string}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {values.hasRenewals === false && (
                        <Box
                          sx={[
                            {
                              display: "flex",
                              flexDirection: isMobileView ? "column" : "row",
                            },
                            isMobileView
                              ? { marginBottom: theme.spacing(3) }
                              : { marginBottom: theme.spacing(2) },
                          ]}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              width: theme.spacing(30),
                            }}
                          >
                            <DateFormField
                              field={{
                                type: "date",
                                field: "initialStartDateTime",
                                label: getStartDateLabel(values.status),
                                maximum: () =>
                                  values.status === MhaStatus.Section3
                                    ? dayjs()
                                    : dayjs().add(2, "months"),
                              }}
                              fieldProps={{
                                ...fieldProps,
                                setValues: (values: any) => {
                                  const isValidInitialStartDate = dayjs(
                                    values.initialStartDateTime,
                                  ).isValid();

                                  if (isValidInitialStartDate) {
                                    void setFieldTouched(
                                      "startDateTime",
                                      true,
                                      false,
                                    );
                                    void setFieldTouched(
                                      "expiryDateTime",
                                      true,
                                      false,
                                    );
                                  }

                                  void setValues({
                                    ...values,
                                    initialStartDateTime:
                                      isValidInitialStartDate
                                        ? dayjs(
                                            values.initialStartDateTime,
                                          ).toISOString()
                                        : values.initialStartDateTime,
                                    startDateTime: values.initialStartDateTime,
                                    expiryDateTime: setExpiryDateTime(
                                      values.status,
                                      values.initialStartDateTime,
                                      0,
                                    ),
                                    expiryOverrideDateTime: undefined,
                                    expiryOverrideReason: undefined,
                                    expiryOverrideReasonDescription: undefined,
                                  });
                                },
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              minWidth: theme.spacing(32),
                            }}
                          >
                            <Box
                              data-testid="currentEndDate"
                              sx={[
                                {
                                  display: "flex",
                                  flexDirection: "column",
                                },
                                isMobileView
                                  ? {}
                                  : { marginLeft: theme.spacing(6) },
                              ]}
                            >
                              <ReadOnlyContent
                                label={getEndDateLabel(values.status)}
                                content={[
                                  formatDateTime(
                                    values.expiryOverrideDateTime ??
                                      values.expiryDateTime,
                                    "DD/MM/YY",
                                  ),
                                ]}
                                isActive
                                useMarginTop={true}
                              />

                              {touched.expiryDateTime &&
                                errors.expiryDateTime && (
                                  <HelperText
                                    errorMessage={
                                      errors.expiryDateTime as string
                                    }
                                  />
                                )}
                            </Box>
                            <ExpiryOverrideControls
                              values={values}
                              fieldProps={fieldProps}
                              expiryCanBeOverridden={expiryCanBeOverridden}
                              showExpiryOverrideDialog={
                                showExpiryOverrideDialog
                              }
                              setShowExpiryOverrideDialog={
                                setShowExpiryOverrideDialog
                              }
                            />
                          </Box>
                        </Box>
                      )}

                      {values.hasRenewals === true && (
                        <Box
                          sx={{
                            marginBottom: theme.spacing(2),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              marginBottom: theme.spacing(2),
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: theme.spacing(12),
                              }}
                            >
                              <Dropdown
                                label={getRenewalDropdownLabel(values.status)}
                                name="mhaStatus"
                                selectedValue={
                                  values.numberOfRenewals === undefined
                                    ? ""
                                    : values.numberOfRenewals
                                }
                                values={renewalDropDownOptions}
                                onChange={(value) => {
                                  const numberOfRenewals = value as number;
                                  const {
                                    originalRenewalEndDate,
                                    currentRenewalStartDate,
                                    currentRenewalEndDate,
                                  } = getRenewalDates(
                                    values.initialStartDateTime,
                                    numberOfRenewals,
                                  );
                                  void setTouched({}, false);
                                  void setValues({
                                    ...values,
                                    numberOfRenewals: value,
                                    originalRenewalExpiry:
                                      originalRenewalEndDate,
                                    expiryDateTime: currentRenewalEndDate,
                                    startDateTime: currentRenewalStartDate,
                                    expiryOverrideDateTime: undefined,
                                    expiryOverrideReason: undefined,
                                    expiryOverrideReasonDescription: undefined,
                                  });
                                }}
                                errorMessage={
                                  touched.numberOfRenewals &&
                                  errors.numberOfRenewals
                                    ? (errors.numberOfRenewals as string)
                                    : undefined
                                }
                              />
                            </Box>
                          </Box>
                          <Box
                            sx={[
                              {
                                display: "flex",
                                flexDirection: isMobileView ? "column" : "row",
                              },
                              isMobileView
                                ? { marginBottom: theme.spacing(3) }
                                : { marginBottom: theme.spacing(2) },
                            ]}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                minWidth: theme.spacing(32),
                              }}
                            >
                              <DateFormField
                                field={{
                                  type: "date",
                                  field: "initialStartDateTime",
                                  label: getOriginalStartDateLabel(
                                    values.status,
                                  ),
                                  maximum: () => dayjs(),
                                  disabled: !values.numberOfRenewals,
                                }}
                                fieldProps={{
                                  ...fieldProps,
                                  setValues: (values: any) => {
                                    const {
                                      originalRenewalEndDate,
                                      currentRenewalStartDate,
                                      currentRenewalEndDate,
                                    } = getRenewalDates(
                                      values.initialStartDateTime,
                                      values.numberOfRenewals,
                                    );

                                    const isValidInitialStartDate = dayjs(
                                      values.initialStartDateTime,
                                    ).isValid();

                                    if (isValidInitialStartDate) {
                                      // Mark these fields as touched to show error messages
                                      void setFieldTouched(
                                        "startDateTime",
                                        true,
                                        false,
                                      );
                                      void setFieldTouched(
                                        "expiryDateTime",
                                        true,
                                        false,
                                      );
                                    }

                                    void setValues({
                                      ...values,
                                      initialStartDateTime:
                                        isValidInitialStartDate
                                          ? dayjs(
                                              values.initialStartDateTime,
                                            ).toISOString()
                                          : values.initialStartDateTime,
                                      originalRenewalExpiry:
                                        originalRenewalEndDate,
                                      expiryDateTime: currentRenewalEndDate,
                                      startDateTime: currentRenewalStartDate,
                                      expiryOverrideDateTime: undefined,
                                      expiryOverrideReason: undefined,
                                      expiryOverrideReasonDescription:
                                        undefined,
                                    });
                                  },
                                }}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                minWidth: theme.spacing(32),
                              }}
                            >
                              {getReadOnlyContent(
                                getOriginalEndDateLabel(values.status),
                                values.originalRenewalExpiry,
                                "DD/MM/YY",
                                "originalRenewalExpiry",
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={[
                              {
                                display: "flex",
                                flexDirection: isMobileView ? "column" : "row",
                              },
                              isMobileView
                                ? { marginBottom: theme.spacing(3) }
                                : { marginBottom: theme.spacing(2) },
                            ]}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                minWidth: theme.spacing(32),
                              }}
                            >
                              <Box
                                data-testid="currentStartDate"
                                sx={[
                                  {
                                    display: "flex",
                                    flexDirection: "column",
                                  },
                                  isMobileView
                                    ? { marginBottom: theme.spacing(3) }
                                    : { marginLeft: "none" },
                                ]}
                              >
                                <ReadOnlyContent
                                  label={getStartDateLabel(values.status)}
                                  content={[
                                    formatDateTime(
                                      values.startDateTime,
                                      "DD/MM/YY",
                                    ),
                                  ]}
                                  isActive
                                />
                                {touched.startDateTime &&
                                  errors.startDateTime && (
                                    <HelperText
                                      errorMessage={
                                        errors.startDateTime as string
                                      }
                                    />
                                  )}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                minWidth: theme.spacing(32),
                              }}
                            >
                              <Box
                                data-testid="currentEndDate"
                                sx={[
                                  {
                                    display: "flex",
                                    flexDirection: "column",
                                  },
                                  isMobileView
                                    ? {}
                                    : { marginLeft: theme.spacing(6) },
                                ]}
                              >
                                <ReadOnlyContent
                                  label={getEndDateLabel(values.status)}
                                  content={[
                                    formatDateTime(
                                      values.expiryOverrideDateTime ??
                                        values.expiryDateTime,
                                      "DD/MM/YY",
                                    ),
                                  ]}
                                  isActive
                                />
                                {touched.expiryDateTime &&
                                  errors.expiryDateTime && (
                                    <HelperText
                                      errorMessage={
                                        errors.expiryDateTime as string
                                      }
                                    />
                                  )}
                              </Box>
                              <ExpiryOverrideControls
                                values={values}
                                fieldProps={fieldProps}
                                expiryCanBeOverridden={expiryCanBeOverridden}
                                showExpiryOverrideDialog={
                                  showExpiryOverrideDialog
                                }
                                setShowExpiryOverrideDialog={
                                  setShowExpiryOverrideDialog
                                }
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {isNotDetained && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          marginBottom: theme.spacing(2),
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <FormLabel
                            label={t("pages.editMHAStatus.notDetainedReason")}
                          />
                          <RadioGroup
                            aria-labelledby="radio-buttons-group-label"
                            name="episodeOfCareDischargeReason"
                            defaultValue={null}
                          >
                            {dischargeReasonOptions.map(({ reason, label }) => (
                              <FormControlLabel
                                key={reason}
                                control={
                                  <Radio sx={{ color: "primary.main" }} />
                                }
                                label={label}
                                value={reason}
                                checked={
                                  values.episodeOfCareDischargeReason === reason
                                }
                                name="episodeOfCareDischargeReason"
                                onChange={() => {
                                  // reset when moving between discharge reason
                                  void setTouched({}, false);
                                  void setValues({
                                    ...values,
                                    episodeOfCareDischargeReason: reason,
                                    hasRenewals: false,
                                    startDateTime: undefined,
                                    expiryDateTime: undefined,
                                    numberOfRenewals: undefined,
                                    episodeOfCareDischargeDateTime: undefined,
                                    hospital: undefined,
                                    placeOfSafety: undefined,
                                    section136Context: undefined,
                                    originalRenewalExpiry: undefined,
                                    initialStartDateTime: undefined,
                                    expiryOverrideDateTime: undefined,
                                    expiryOverrideReason: undefined,
                                    expiryOverrideReasonDescription: undefined,
                                  });
                                }}
                              />
                            ))}
                          </RadioGroup>
                          {touched.episodeOfCareDischargeReason &&
                            errors.episodeOfCareDischargeReason && (
                              <HelperText
                                errorMessage={
                                  errors.episodeOfCareDischargeReason as string
                                }
                              />
                            )}
                        </Box>
                      </Box>
                      {isNotDetained && values.episodeOfCareDischargeReason && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            marginBottom: theme.spacing(2),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <DateTimeFormField
                              field={{
                                label: t(
                                  "pages.editMHAStatus.dateTimeDischarge",
                                ),
                                type: "date-time",
                                field: "episodeOfCareDischargeDateTime",
                                showDateTimeMessage: false,
                                maximum: () => dayjs(),
                              }}
                              fieldProps={fieldProps}
                            />
                          </Box>
                        </Box>
                      )}
                    </>
                  )}

                  {isPendingState && (
                    <Banner
                      dense
                      bannerType={BannerList.WARNING}
                      body={[
                        isPendingCto
                          ? t("pages.editMHAStatus.pendingCTO")
                          : isRenewalPendingCTO
                            ? t("pages.editMHAStatus.renewalPendingCTO")
                            : t("pages.editMHAStatus.renewalPendingSection3"),
                      ]}
                    />
                  )}
                </>
                {showHospital && (
                  <>
                    <Divider
                      sx={{
                        marginBottom: theme.spacing(2),
                      }}
                    />
                    <div>
                      <HospitalPicker
                        field={{
                          type: "hospital-picker",
                          field: "hospital",
                          label:
                            values.status === MhaStatus.NotDetained
                              ? t("pages.editMHAStatus.previousHospital")
                              : t("pages.editMHAStatus.hospital"),
                          tooltip: t("pages.editMHAStatus.hospitalToolTip"),
                        }}
                        fieldProps={fieldProps}
                        trustAutoSelectedMessageOverride={t(
                          "pages.editMHAStatus.trustAutoSelected",
                        )}
                      />
                    </div>
                  </>
                )}
                {isS136Discharge && (
                  <>
                    <Divider
                      sx={{
                        marginBottom: theme.spacing(2),
                      }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {placeOfSafetyFields(
                        t("pages.editMHAStatus.previousPlaceOfSafety"),
                      )}
                    </Box>
                  </>
                )}
              </Container>
              <FormFooterSection
                customFooterBackgroundColor={theme.palette.common.white}
                onSave={submitForm}
                saveLabel={
                  isScrutiny
                    ? t("pages.editMHAStatus.continue")
                    : t("pages.editMHAStatus.updateStatusBtn")
                }
                discardLabel={t("pages.editMHAStatus.discardBtn")}
                onCancel={() => {
                  if (isScrutiny) {
                    navigate(
                      routeFns.formContextPage(
                        formContext!.id,
                        state.patientId,
                      ),
                    );
                  } else {
                    navigate(routeFns.patientHome(state.patientId));
                  }
                }}
                disableDiscard={isSubmitting}
                disableSubmit={
                  isScrutiny ? isSubmitting : isSubmitting || !dirty
                }
                minHeight
              />
            </>
          );
        }}
      </Formik>
    </Box>
  );
}
